/**
 * Request Manager
 *
 * Provides policy-backed in-flight request controls using native `fetch` and `AbortController`.
 * Domain services should choose a policy and key; route components call named service functions
 * rather than invoking this module directly.
 *
 * Policy behaviors
 * ─────────────────
 * single-flight    – If a request for this key is already in-flight, return the existing promise.
 *                    Use for critical mutations (freeze, schedule, form submit) that must not run
 *                    more than once concurrently.
 *
 * dedupe-in-flight – Same reuse behavior as single-flight, but semantically for idempotent reads
 *                    (config lookups, reference data) that are safe to share across callers.
 *
 * latest-wins      – Abort the previous in-flight request for this key and start a fresh one.
 *                    The aborted request's promise rejects with a DOMException("AbortError");
 *                    services should catch it and return early without updating state.
 *                    Use for provider search and slot/date lookups where only the newest result
 *                    should update the UI.
 *
 * allow-duplicates – Always send the request; no deduplication or storage. Use for logging,
 *                    analytics, and any call that represents a distinct event.
 */

export type RequestPolicy =
  | "single-flight"
  | "latest-wins"
  | "dedupe-in-flight"
  | "allow-duplicates";

export interface ManagedRequestOptions<T> {
  /** Domain key that identifies the logical operation (e.g. `"freeze:${userUuid}:${practiceId}"`). */
  key: string;
  policy: RequestPolicy;
  /** Receives an AbortSignal when the policy supports cancellation. */
  request: (signal?: AbortSignal) => Promise<T>;
}

// Module-level maps — intentionally not reset between renders.
const inFlightMap = new Map<string, Promise<unknown>>();
const controllerMap = new Map<string, AbortController>();

// ─── In-flight count tracking ─────────────────────────────────────────────────
// Tracks the number of managed requests currently in flight (excludes allow-duplicates).
// Used by AppBody to block browser refresh while requests are pending.
let inFlightRequestCount = 0;
type InFlightListener = () => void;
const inFlightListeners = new Set<InFlightListener>();

function incrementInFlight(): void {
  inFlightRequestCount++;
  for (const listener of inFlightListeners) listener();
}

function decrementInFlight(): void {
  inFlightRequestCount = Math.max(0, inFlightRequestCount - 1);
  for (const listener of inFlightListeners) listener();
}

/** Returns true if one or more managed requests are currently in flight. */
export function isRequestInFlight(): boolean {
  return inFlightRequestCount > 0;
}

/**
 * Subscribe to in-flight count changes. The listener is called whenever a
 * managed request starts or finishes. Returns an unsubscribe function.
 */
export function subscribeToInFlight(listener: InFlightListener): () => void {
  inFlightListeners.add(listener);
  return () => {
    inFlightListeners.delete(listener);
  };
}
// ─────────────────────────────────────────────────────────────────────────────

// ─── Single-flight sessionStorage persistence ────────────────────────────────
// single-flight mutations (freeze, schedule) persist their in-flight key to
// sessionStorage so the guard survives a browser refresh. sessionStorage is
// cleared when the tab is closed, so stale keys can only originate from a
// same-tab page refresh while the request was in-flight.
const SF_STORAGE_PREFIX = "obc-rm-sf:";

function sfPersist(key: string): void {
  try {
    sessionStorage.setItem(SF_STORAGE_PREFIX + key, "1");
  } catch {
    // sessionStorage unavailable (private-browsing quota, etc.) — degrade gracefully.
  }
}

function sfClear(key: string): void {
  try {
    sessionStorage.removeItem(SF_STORAGE_PREFIX + key);
  } catch {
    // No-op.
  }
}

function sfIsStale(key: string): boolean {
  try {
    return sessionStorage.getItem(SF_STORAGE_PREFIX + key) !== null;
  } catch {
    return false;
  }
}
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Calls fn() synchronously and returns its Promise. If fn() throws before
 * returning a Promise (sync throw), returns Promise.reject(error) so the
 * caller's .finally() chain still runs for cleanup. Calling fn() synchronously
 * (rather than deferring to a microtask) preserves abort-signal timing.
 */
function callSyncToPromise<T>(fn: () => Promise<T>): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    fn().then(resolve, reject);
  });
}

/**
 * Run a request with an explicit deduplication / cancellation policy.
 * See module JSDoc for policy behavior details.
 */
export function runManagedRequest<T>(options: ManagedRequestOptions<T>): Promise<T> {
  const { key, policy, request } = options;

  if (policy === "allow-duplicates") {
    return request();
  }

  if (policy === "single-flight" || policy === "dedupe-in-flight") {
    const existing = inFlightMap.get(key);
    if (existing !== undefined) {
      return existing as Promise<T>;
    }

    // single-flight only: if a sessionStorage marker exists but there is no
    // current in-flight promise, the request was interrupted by a page refresh.
    // Reject immediately so callers do not silently re-submit a mutation whose
    // server-side outcome is unknown. The marker is cleared so the next
    // explicit user action can retry normally.
    if (policy === "single-flight" && sfIsStale(key)) {
      sfClear(key);
      return Promise.reject(
        Object.assign(
          new Error(
            `single-flight: request "${key}" was interrupted by a page refresh — outcome unknown`,
          ),
          { code: "REFRESH_INTERRUPTED" as const },
        ),
      );
    }

    if (policy === "single-flight") {
      sfPersist(key);
    }

    incrementInFlight();
    const rawPromise = callSyncToPromise(() => request());
    const promise = rawPromise.finally(() => {
      inFlightMap.delete(key);
      decrementInFlight();
      if (policy === "single-flight") {
        sfClear(key);
      }
    });

    inFlightMap.set(key, promise);
    return promise;
  }

  // latest-wins: abort previous request for this key and start fresh.
  const prevController = controllerMap.get(key);
  if (prevController === undefined) {
    // First request for this key — increment the counter.
    incrementInFlight();
  } else {
    prevController.abort();
    // The existing request will be replaced; don't change the counter — still 1 in-flight for this key.
  }

  const controller = new AbortController();
  controllerMap.set(key, controller);

  const rawPromise = callSyncToPromise(() => request(controller.signal));

  return rawPromise.finally(() => {
    // Only clean up if this controller is still the current one for the key.
    if (controllerMap.get(key) === controller) {
      controllerMap.delete(key);
      decrementInFlight();
    }
  });
}
